import { useEffect, useState } from "react";
import { Bell, X, Loader2 } from "lucide-react";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const DISMISS_KEY = "energyworld.push_banner_dismissed_at";
const DISMISS_HOURS = 24 * 7; // ricompare dopo 7 giorni

export default function PushPermissionBanner() {
  const { user } = useAuth();
  const { supported, canSubscribe, isIOS, standalone, subscribed, permission, busy, subscribe } = usePushNotifications();
  const [dismissed, setDismissed] = useState(() => {
    try {
      const t = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (!t) return false;
      return (Date.now() - t) < DISMISS_HOURS * 3600 * 1000;
    } catch { return false; }
  });

  // Don't show at all if: no user, not supported, already subscribed, permission denied, user dismissed
  if (!user) return null;
  if (!supported) return null;
  if (subscribed) return null;
  if (permission === "denied") return null;
  if (dismissed) return null;

  // On iOS, only show if the app is actually installed to home. Otherwise it's useless.
  if (isIOS && !standalone) return null;
  if (!canSubscribe) return null;

  const onDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setDismissed(true);
  };

  const onEnable = async () => {
    const res = await subscribe();
    if (res.ok) {
      toast.success("Notifiche attivate su questo dispositivo");
      setDismissed(true);
    } else if (res.reason === "denied") {
      toast.error("Permesso notifiche negato");
      setDismissed(true);
    } else if (res.reason !== "unsupported") {
      toast.error("Impossibile attivare le notifiche");
    }
  };

  return (
    <div className="fixed inset-x-3 bottom-3 z-[60] max-w-md mx-auto rounded-xl bg-blue-900 text-white shadow-2xl ring-1 ring-blue-700" data-testid="push-banner">
      <div className="flex items-start gap-3 p-4">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-blue-800 flex items-center justify-center">
          <Bell className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">Attiva le notifiche</div>
          <p className="text-sm text-blue-100 mt-0.5">
            Ricevi avvisi immediati su nuovi lavori, preventivi, messaggi e offerte.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={onEnable}
              disabled={busy}
              className="inline-flex items-center gap-2 bg-white text-blue-900 font-semibold px-3 py-1.5 rounded-lg text-sm hover:bg-blue-50 disabled:opacity-60"
              data-testid="push-banner-enable"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
              Attiva
            </button>
            <button
              onClick={onDismiss}
              className="text-blue-100 hover:text-white text-sm px-2 py-1.5"
              data-testid="push-banner-dismiss"
            >
              Più tardi
            </button>
          </div>
        </div>
        <button onClick={onDismiss} className="shrink-0 text-blue-200 hover:text-white" aria-label="Chiudi" data-testid="push-banner-close">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
